import { motion } from "framer-motion";
import type { ComparisonData } from "../types";

/**
 * Beyond Sure — multi-insurer comparison block.
 *
 * Use case: the assistant has compared ~6 insurers and wants to show
 * them side-by-side. Responsive:
 *   - sm+ → table layout (insurer | premium | cover | claim ratio | fit)
 *   - mobile → stacked cards (one row per insurer, with the fit bar
 *     spanning the bottom).
 *
 * "fit" 0-100 drives a green bar — Stripe/Linear pattern. The eye
 * jumps to the highest bar, which is exactly what we want.
 */

interface ComparisonBlockProps {
  data: ComparisonData;
}

export function ComparisonBlock({ data }: ComparisonBlockProps) {
  const sorted = [...data.rows].sort((a, b) => b.fit - a.fit);
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
      data-testid="message-comparison"
      className={[
        "rounded-[14px] p-4 sm:p-5",
        "bg-[rgb(255_255_255_/_0.86)] backdrop-blur-sm",
        "border border-[var(--color-ink-08)]",
        "shadow-[0_6px_22px_-14px_rgba(20,75,51,0.32)]",
      ].join(" ")}
    >
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <p className="bsure-caps text-[10.5px] font-semibold tracking-[0.16em] text-[var(--color-brand-deep)]">
          Side-by-side
        </p>
        <p className="text-[11.5px] text-[var(--color-ink-40)] truncate">
          {data.query}
        </p>
      </div>

      {/* Desktop / tablet table */}
      <div className="hidden sm:block">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-[var(--color-ink-08)]">
              {["Insurer", "Premium / yr", "Cover", "Claim ratio", "Fit"].map((h) => (
                <th
                  key={h}
                  className="bsure-caps text-[9.5px] font-semibold tracking-[0.14em] text-[var(--color-ink-40)] py-2 pr-3"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => (
              <tr
                key={r.insurer}
                className={[
                  "text-[13.5px] text-[var(--color-ink)]",
                  i === 0 ? "font-semibold" : "",
                ].join(" ")}
              >
                <td className="py-2.5 pr-3">{r.insurer}</td>
                <td className="py-2.5 pr-3 tabular-nums">{r.premium}</td>
                <td className="py-2.5 pr-3 tabular-nums">{r.cover}</td>
                <td className="py-2.5 pr-3 tabular-nums">{r.claimRatio}</td>
                <td className="py-2.5 w-[140px]">
                  <FitBar value={r.fit} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile stack */}
      <div className="sm:hidden flex flex-col gap-2">
        {sorted.map((r, i) => (
          <div
            key={r.insurer}
            className={[
              "rounded-[10px] border border-[var(--color-ink-08)] px-3 py-2.5",
              "bg-[var(--color-paper)]",
              i === 0 ? "ring-1 ring-[var(--color-brand-deep)]" : "",
            ].join(" ")}
          >
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-[13.5px] font-semibold text-[var(--color-ink)]">
                {r.insurer}
              </p>
              <p className="text-[12px] tabular-nums text-[var(--color-ink-60)]">
                {r.premium}
              </p>
            </div>
            <div className="mt-1 grid grid-cols-2 gap-2 text-[11.5px] text-[var(--color-ink-60)]">
              <span>Cover · {r.cover}</span>
              <span>Claim ratio · {r.claimRatio}</span>
            </div>
            <div className="mt-2">
              <FitBar value={r.fit} />
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

function FitBar({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div className="flex items-center gap-2">
      <span className="relative flex-1 h-[6px] rounded-full overflow-hidden bg-[var(--color-ink-08)]">
        <motion.span
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.6, ease: [0.32, 0.72, 0, 1] }}
          className="absolute inset-y-0 left-0 rounded-full"
          style={{
            background:
              "linear-gradient(90deg, var(--color-brand-deep), var(--color-brand) 60%, var(--color-brand-vivid))",
          }}
        />
      </span>
      <span className="text-[11px] font-semibold tabular-nums text-[var(--color-ink-60)] w-[26px] text-right">
        {pct}
      </span>
    </div>
  );
}

export default ComparisonBlock;
